import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    OutlinedInput,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckBox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import React from "react";
import { Controller } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { Link as RouterLink } from "react-router-dom";
import LogoUlo from "../../../components/LogoUlo/LogoUlo";
import { RegisterFormModel } from "../hooks/useRegisterForm";

interface RegisterFormProps {
    registerForm: RegisterFormModel;
}

const RegisterForm = ({ registerForm }: RegisterFormProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isLaptopScreen = useMediaQuery(theme.breakpoints.up("md"));
    const {
        agreedToTerms,
        captchaSiteKey,
        form,
        isLoadingKota,
        isLoadingProvinsi,
        kotaOptions,
        loading,
        onCaptchaChange,
        onCaptchaExpired,
        onProvinceChange,
        onSubmit,
        provinsiOptions,
        setAgreedToTerms,
        showPassword,
        showPasswordConfirmation,
        togglePasswordConfirmationVisibility,
        togglePasswordVisibility,
    } = registerForm;

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            paddingY={3}
            paddingX={isTabletScreen ? 2 : 4}
            sx={{
                backgroundColor: "#fff",
                borderRadius: "inherit",
            }}
        >
            <Link href="https://ukmlokal.com/" rel="noreferrer" target="_blank">
                <LogoUlo width="100px" />
            </Link>
            {isLaptopScreen ? (
                <Typography
                    marginTop={2}
                    component="div"
                    textAlign="center"
                    variant="h6"
                    fontWeight="bold"
                >
                    Bergabung dengan UKM{" "}
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: "#20965F" }}
                        display="inline"
                    >
                        LOKAL
                    </Typography>
                </Typography>
            ) : (
                <React.Fragment>
                    <Typography
                        marginTop={2}
                        textAlign="center"
                        variant="h6"
                        fontWeight="bold"
                    >
                        Bergabung dengan
                    </Typography>
                    <Typography
                        component="div"
                        textAlign="center"
                        variant="h6"
                        fontWeight="bold"
                    >
                        UKM{" "}
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ color: "#20965F" }}
                            display="inline"
                        >
                            LOKAL
                        </Typography>
                    </Typography>
                </React.Fragment>
            )}
            <form
                style={{
                    width: "100%",
                    maxWidth: "500px",
                    minWidth: "200px",
                }}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="#000"
                    fontSize="16px"
                    sx={{
                        width: "fit-content",
                        marginTop: 3,
                        borderBottom: "1px solid #20965F",
                        marginBottom: 2,
                    }}
                >
                    Daftar
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="body2">Nama</Typography>
                        <Controller
                            name="nama"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.nama)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="nama"
                                        autoFocus={isLaptopScreen}
                                        placeholder="Contoh: Budi Saputra"
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.nama
                                            ? errors.nama.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Username</Typography>
                        <Controller
                            name="username"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.username)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="username"
                                        placeholder="Contoh: budi93"
                                        size="small"
                                        inputProps={{
                                            style: {
                                                textTransform: "lowercase",
                                            },
                                        }}
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.username
                                            ? errors.username.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Email</Typography>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.email)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="Contoh: budi93@gmail.com"
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.email
                                            ? errors.email.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">No. Telepon</Typography>
                        <Controller
                            name="no_telp"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.no_telp)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="noTelp"
                                        placeholder="831xxxxxx"
                                        type="number"
                                        onWheel={(event) => {
                                            const target =
                                                event.target as HTMLButtonElement;
                                            target.blur();
                                            event.stopPropagation();
                                            setTimeout(() => {
                                                target.focus();
                                            }, 0);
                                        }}
                                        onKeyDown={(event) =>
                                            ["e", "E", "+", "-"].includes(
                                                event.key,
                                            ) && event.preventDefault()
                                        }
                                        size="small"
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{ color: "#000" }}
                                            >
                                                <Typography>+62</Typography>
                                            </InputAdornment>
                                        }
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.no_telp
                                            ? errors.no_telp.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="body2">Provinsi</Typography>
                        <Controller
                            name="provinsi"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    loading={isLoadingProvinsi}
                                    options={provinsiOptions}
                                    getOptionLabel={(option) => option.nama}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    value={field.value ?? undefined}
                                    size="small"
                                    onChange={(_event, newProvinsi) => {
                                        field.onChange(newProvinsi);
                                        onProvinceChange(newProvinsi?.id);
                                    }}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Pilih Provinsi"
                                            error={Boolean(fieldState.error)}
                                            helperText={
                                                fieldState.error
                                                    ? "Pilih satu provinsi"
                                                    : " "
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="body2">Kota</Typography>
                        <Controller
                            name="kota"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    loading={isLoadingKota}
                                    options={kotaOptions}
                                    getOptionLabel={(option) => option.nama}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    value={field.value ?? undefined}
                                    size="small"
                                    onChange={(_event, newKota) => {
                                        field.onChange(newKota);
                                    }}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Pilih Kota"
                                            error={Boolean(fieldState.error)}
                                            helperText={
                                                fieldState.error
                                                    ? "Pilih satu kota"
                                                    : " "
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Alamat</Typography>
                        <Controller
                            name="alamat"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.alamat)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="alamat"
                                        placeholder="Contoh: Jalan Merdeka Raya no. 45"
                                        size="small"
                                        multiline
                                        rows={3}
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.alamat
                                            ? errors.alamat.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="body2">Kata Sandi</Typography>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.password)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="password"
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        placeholder="Minimal 8 karakter"
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={
                                                        togglePasswordVisibility
                                                    }
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.password
                                            ? errors.password.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="body2">
                            Konfirmasi Kata Sandi
                        </Typography>
                        <Controller
                            name="passwordUlang"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.passwordUlang)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="passwordUlang"
                                        type={
                                            showPasswordConfirmation
                                                ? "text"
                                                : "password"
                                        }
                                        placeholder="Minimal 8 karakter"
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={
                                                        togglePasswordConfirmationVisibility
                                                    }
                                                    edge="end"
                                                >
                                                    {showPasswordConfirmation ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.passwordUlang
                                            ? errors.passwordUlang.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2">Nama Toko</Typography>
                        <Controller
                            name="namaToko"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.namaToko)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="namaToko"
                                        placeholder="Contoh: Budi Shop"
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.namaToko
                                            ? errors.namaToko.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Typography variant="body2">
                            Kode Referral (Opsional)
                        </Typography>
                        <Controller
                            name="referral"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(errors.referral)}
                                    sx={{ maxWidth: "500px" }}
                                >
                                    <OutlinedInput
                                        id="referral"
                                        placeholder="Masukkan kode referral"
                                        size="small"
                                        {...field}
                                    />
                                    <FormHelperText>
                                        {errors.referral
                                            ? errors.referral.message
                                            : " "}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>

                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                >
                    <CheckBox
                        checked={agreedToTerms}
                        onChange={(event) => {
                            setAgreedToTerms(event.target.checked);
                        }}
                    />
                    <Typography fontSize={16} fontWeight={400}>
                        Saya setuju dengan{" "}
                        <Link
                            variant="body2"
                            href="/syarat-ketentuan"
                            target="blank"
                            underline="hover"
                            color="#0A0AE1"
                            sx={{
                                textDecoration: "underline",
                                fontWeight: 600,
                                fontSize: 16,
                            }}
                        >
                            syarat dan ketentuan
                        </Link>{" "}
                        yang berlaku
                    </Typography>
                </Stack>

                <Box marginTop={3} display="flex" justifyContent="center">
                    <ReCAPTCHA
                        sitekey={captchaSiteKey}
                        onChange={onCaptchaChange}
                        onExpired={onCaptchaExpired}
                        hl="id"
                    />
                </Box>

                <Button
                    disabled={!agreedToTerms || loading}
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    sx={{ marginTop: 3, maxWidth: "500px" }}
                >
                    Daftar
                </Button>
                <Typography
                    variant="body2"
                    component="div"
                    textAlign="center"
                    sx={{ marginTop: 2 }}
                >
                    Sudah punya akun?{" "}
                    <Link
                        component={RouterLink}
                        to="/"
                        variant="body2"
                        underline="hover"
                        sx={{ cursor: "pointer" }}
                    >
                        Masuk
                    </Link>
                </Typography>
            </form>
        </Box>
    );
};

export default RegisterForm;
